import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchEmployeesAttendance } from '../../store/slices/attendanceSummarySlice';

import { Users, ListChecks, CalendarCheck2, Gauge, Clock3, Timer } from 'lucide-react';

import { StatGrid } from '../../components/dashboard';
import BarChart from '../../components/HRM/attendance-summary/bar-char';
import PieChart from '../../components/HRM/attendance-summary/pie-chart';
import HourDistributionChart from '../../components/HRM/attendance-summary/hour-distribution-chart';
import HorizontalBarList from '../../components/HRM/attendance-summary/horizontal-bar-list';
import AttendanceSummaryTable from '../../components/HRM/attendance-summary/table';
import FiltersBar from '../../components/HRM/attendance-summary/filters-bar';

import {
  buildFilterOptions,
  applyFilters,
  computeKpis,
  buildDailyTrend,
  buildPunchSummary,
  buildByCategory,
  buildInTimeDistribution,
  buildByGrade,
  buildByDesignation,
  buildTopEmployees,
  buildOnTimeVsLate,
  monthRange,
  formatHoursMinutes,
} from '../../utils/attendanceSummaryAnalytics';

const today = new Date();
const DEFAULT_YEAR = today.getFullYear();
const DEFAULT_MONTH = today.getMonth();

export default function AttendanceSummaryDashboard() {
  const dispatch = useDispatch();

  const { rows, loading, error } = useSelector((s) => s.attendanceSummary);

  const [year, setYear] = useState(DEFAULT_YEAR);
  const [activeMonth, setActiveMonth] = useState(DEFAULT_MONTH);
  const [range, setRange] = useState(() => monthRange(DEFAULT_YEAR, DEFAULT_MONTH));
  const [filters, setFilters] = useState({ location: 'All', grade: 'All', category: 'All', designation: 'All' });

  const loadAttendance = useCallback((from, to) => {
    dispatch(fetchEmployeesAttendance({ fromDate: from, toDate: to }));
  }, [dispatch]);


  useEffect(() => {
    loadAttendance(range.from, range.to);
  }, []);

  const handleYearChange = (nextYear) => {
    setYear(nextYear);
    const monthIndex = activeMonth === null ? DEFAULT_MONTH : activeMonth;
    setActiveMonth(monthIndex);
    const next = monthRange(nextYear, monthIndex);
    setRange(next);
    loadAttendance(next.from, next.to);
  };

  const handleMonthSelect = (monthIndex) => {
    setActiveMonth(monthIndex);
    const next = monthRange(year, monthIndex);
    setRange(next);
    loadAttendance(next.from, next.to);
  };

  const handleFromDateChange = (value) => {
    setActiveMonth(null);
    const next = { ...range, from: value };
    setRange(next);
    if (next.from && next.to) loadAttendance(next.from, next.to);
  };

  const handleToDateChange = (value) => {
    setActiveMonth(null);
    const next = { ...range, to: value };
    setRange(next);
    if (next.from && next.to) loadAttendance(next.from, next.to);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filterOptions = useMemo(() => buildFilterOptions(rows), [rows]);
  const filteredRows = useMemo(() => applyFilters(rows, filters), [rows, filters]);

  const kpis = useMemo(() => computeKpis(filteredRows), [filteredRows]);
  const dailyTrend = useMemo(() => buildDailyTrend(filteredRows), [filteredRows]);
  const punchSummary = useMemo(() => buildPunchSummary(filteredRows), [filteredRows]);
  const byCategory = useMemo(() => buildByCategory(filteredRows), [filteredRows]);
  const inTimeDistribution = useMemo(() => buildInTimeDistribution(filteredRows), [filteredRows]);
  const byGrade = useMemo(() => buildByGrade(filteredRows), [filteredRows]);
  const byDesignation = useMemo(() => buildByDesignation(filteredRows), [filteredRows]);
  const topEmployees = useMemo(() => buildTopEmployees(filteredRows), [filteredRows]);
  const onTimeVsLate = useMemo(() => buildOnTimeVsLate(filteredRows), [filteredRows]);

  // ── KPI cards — this page decides what to show; StatGrid decides how it looks
  const stats = [
    { label: 'Staff Attended', value: (kpis.staffAttended ?? 0).toLocaleString(), sub: 'At least 1 day in period', icon: Users, tone: 'primary' },
    { label: 'Total Records', value: (kpis.totalRecords ?? 0).toLocaleString(), sub: 'Attendance swipes', icon: ListChecks, tone: 'blue' },
    { label: 'Active Days', value: (kpis.activeDays ?? 0).toLocaleString(), sub: 'Days with attendance', icon: CalendarCheck2, tone: 'violet' },
    { label: 'Avg / Day', value: (kpis.avgPerDay ?? 0).toFixed(1), sub: 'Employees per working day', icon: Gauge, tone: 'amber' },
    { label: 'On Time', value: (kpis.onTimeCount ?? 0).toLocaleString(), sub: `Late ${(kpis.lateCount ?? 0).toLocaleString()} · by shift start`, icon: Clock3, tone: 'green' },
    { label: 'Avg Work Hours', value: formatHoursMinutes(kpis.avgWorkMinutes ?? 0), sub: 'Full-punch records only', icon: Timer, tone: 'sky' },
  ];

  return (
    <div className="p-6 space-y-4">
      {/* ── Filters ────────────────────────────────────────────── */}
      <FiltersBar
        year={year}
        onYearChange={handleYearChange}
        activeMonth={activeMonth}
        onMonthSelect={handleMonthSelect}
        fromDate={range.from}
        toDate={range.to}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
        filterOptions={filterOptions}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {loading && !rows.length ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-400 text-sm">Loading attendance data...</div>
        </div>
      ) : (
        <>
          {/* ── KPI cards ─────────────────────────────────────── */}
          <StatGrid stats={stats} columns={6} />

          {/* ── Row 1: Daily trend / Punch summary / Category / Hourly distribution ── */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-2">
              <BarChart data={dailyTrend} />
            </div>
            <PieChart title="Punch Summary" data={punchSummary} />
            <PieChart title="By Employment Category" data={byCategory} />
            <HourDistributionChart data={inTimeDistribution} />
          </div>

          {/* ── Row 2: Grade / Designation / Top employees / On time vs late ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <HorizontalBarList title="Attendance by Grade" data={byGrade} color="#7c3aed" />
            <HorizontalBarList title="Attendance by Designation" data={byDesignation} color="#0d9488" />
            <AttendanceSummaryTable data={topEmployees} />
            <PieChart title="On Time vs Late Arrivals" data={onTimeVsLate} />
          </div>
        </>
      )}
    </div>
  );
}